<script setup lang="ts">
import type { TutorialVideo } from '~/utils/crystallize/tutorial-video'

const { video } = defineProps<{
  video: TutorialVideo
}>()

const chapters = computed(
  () => video.components.chapters?.filter(
    (c): c is { timestamp: string, label: string } => !!c.label && !!c.timestamp,
  ),
)

const listRef = useTemplateRef('list')

const { arrivedState } = useScroll(listRef)

const arrivedAttr = computed(() => {
  if (arrivedState.left) {
    return arrivedState.right ? 'both' : 'left'
  }
  else if (arrivedState.right) {
    return 'right'
  }
  else {
    return 'none'
  }
})
</script>

<template>
  <ol
    v-if="chapters?.length"
    ref="list"
    class="flex gap-2 items-center overflow-x-scroll no-scrollbar relative"
    :data-arrived="arrivedAttr"
  >
    <li v-for="chapter in chapters" :key="chapter.timestamp" class="flex items-center">
      <NuxtLink class="no-underline rounded-lg text-[10px] leading-none md:text-sm bg-blurple-50 px-xs py-1.5 md:px-s md:py-2.5xs" :to="{ path: video.path, query: { timestamp: chapter.timestamp } }">
        {{ chapter.label }}
      </NuxtLink>
    </li>
  </ol>
</template>

<style scoped>
ol {
  --fade: 48px;
  --gradient-color: var(--bg-color, white);

  &[data-arrived='both'] {
    mask-image: none;
  }

  &[data-arrived='none'] {
    mask-image: linear-gradient(
      to right,
      transparent 0,
      var(--gradient-color) var(--fade),
      var(--gradient-color) calc(100% - var(--fade)),
      transparent 100%
    );
  }

  &[data-arrived='left'] {
    mask-image: linear-gradient(to right, var(--gradient-color) calc(100% - var(--fade)), transparent 100%);
  }

  &[data-arrived='right'] {
    mask-image: linear-gradient(to left, var(--gradient-color) calc(100% - var(--fade)), transparent 100%);
  }
}
</style>
